import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { SectionFrame, formatCurrency } from './SharedFrames';
import { todayIstDateString, useHistoricalTrades } from '../hooks/useHistoricalTrades';
import { defaultOverviewFromDate, useDailyPnlSummary } from '../hooks/useDailyPnlSummary';
import PnlCalendarHeatMap from './PnlCalendarHeatMap';
import HistoricalTradesTable from './HistoricalTradesTable';

const TAB_OVERVIEW = 'overview';
const TAB_TRADES = 'trades';

const UNIVERSE_OPTIONS = [
    { key: 'ALL', label: 'All' },
    { key: 'BANKNIFTY', label: 'Bank Nifty' },
    { key: 'NIFTY', label: 'Nifty 50' },
];

function formatSelectedDayLabel(dateStr) {
    if (!dateStr) return '';
    const dt = new Date(`${dateStr}T12:00:00`);
    return dt.toLocaleDateString('en-IN', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

const TradeArchivePanel = ({ strategyId, strategyLabel, defaultUniverse = 'ALL', enabled = true }) => {
    const defaultDay = todayIstDateString();
    const [tab, setTab] = useState(TAB_OVERVIEW);
    const [from, setFrom] = useState(defaultOverviewFromDate(7));
    const [to, setTo] = useState(defaultDay);
    const [selectedDay, setSelectedDay] = useState(null);
    const [status, setStatus] = useState('closed');
    const [universe, setUniverse] = useState(defaultUniverse);

    const isVyasa = strategyId === 'vyasa';
    const showUniverseFilter = strategyId === 'chanakya_inverse';
    const universeParam = showUniverseFilter ? universe : null;

    // Follow the desk-level universe toggle; the archive dropdown can still diverge.
    useEffect(() => {
        setUniverse(defaultUniverse);
    }, [defaultUniverse]);
    const overviewEnabled = enabled && tab === TAB_OVERVIEW;
    const tradesTabEnabled = enabled && tab === TAB_TRADES;
    const selectedDayEnabled = enabled && tab === TAB_OVERVIEW && Boolean(selectedDay);
    const showStatusFilter = !isVyasa && (tab === TAB_TRADES || selectedDayEnabled);

    const {
        trades: rangeTrades,
        totals: rangeTradeTotals,
        loading: rangeTradesLoading,
        error: rangeTradesError,
        refresh: refreshRangeTrades,
    } = useHistoricalTrades({
        strategyId,
        from,
        to,
        status: isVyasa ? 'closed' : status,
        universe: universeParam,
        enabled: tradesTabEnabled,
    });

    const {
        trades: dayTrades,
        totals: dayTradeTotals,
        loading: dayTradesLoading,
        error: dayTradesError,
        refresh: refreshDayTrades,
    } = useHistoricalTrades({
        strategyId,
        from: selectedDay,
        to: selectedDay,
        status: isVyasa ? 'closed' : status,
        universe: universeParam,
        enabled: selectedDayEnabled,
    });

    const {
        days,
        totals: dailyTotals,
        loading: dailyLoading,
        error: dailyError,
        refresh: refreshDaily,
    } = useDailyPnlSummary({
        strategyId,
        from,
        to,
        universe: universeParam,
        enabled: overviewEnabled,
    });

    useEffect(() => {
        if (!selectedDay) return;
        if (selectedDay < from || selectedDay > to) {
            setSelectedDay(null);
        }
    }, [from, to, selectedDay]);

    const selectedDaySummary = useMemo(
        () => (selectedDay ? days[selectedDay] : null),
        [days, selectedDay]
    );

    const handleApply = () => {
        if (tab === TAB_OVERVIEW) {
            refreshDaily();
            if (selectedDay) refreshDayTrades();
        } else {
            refreshRangeTrades();
        }
    };

    const handleToday = () => {
        setFrom(defaultDay);
        setTo(defaultDay);
        setSelectedDay(defaultDay);
    };

    const handleDayClick = (date) => {
        setSelectedDay(date);
    };

    if (!enabled) {
        return null;
    }

    return (
        <SectionFrame
            accent="strategy"
            eyebrow="Trade Archive"
            title="Historical Trades"
            description={`Macro daily P&L calendar and per-trade detail for ${strategyLabel}. Sessions are IST from 05:00.`}
        >
            <Stack spacing={2}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', md: 'flex-end' }}
                    flexWrap="wrap"
                    useFlexGap
                >
                    <TextField
                        label="From"
                        type="date"
                        size="small"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                    />
                    <TextField
                        label="To"
                        type="date"
                        size="small"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                    />
                    {showUniverseFilter ? (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="hist-universe-label">Index</InputLabel>
                            <Select
                                labelId="hist-universe-label"
                                label="Index"
                                value={universe}
                                onChange={(e) => setUniverse(e.target.value)}
                            >
                                {UNIVERSE_OPTIONS.map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}
                    {showStatusFilter ? (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="hist-status-label">Show</InputLabel>
                            <Select
                                labelId="hist-status-label"
                                label="Show"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value="closed">Closed only</MenuItem>
                                <MenuItem value="all">All (incl. open)</MenuItem>
                            </Select>
                        </FormControl>
                    ) : null}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleApply}
                        disabled={
                            tab === TAB_OVERVIEW
                                ? dailyLoading || (selectedDayEnabled && dayTradesLoading)
                                : rangeTradesLoading
                        }
                    >
                        {(
                            tab === TAB_OVERVIEW
                                ? dailyLoading || (selectedDayEnabled && dayTradesLoading)
                                : rangeTradesLoading
                        )
                            ? 'Loading…'
                            : 'Apply'}
                    </Button>
                    <Button variant="text" size="small" onClick={handleToday}>
                        Today
                    </Button>
                </Stack>

                <Tabs
                    value={tab}
                    onChange={(_, value) => setTab(value)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Overview" value={TAB_OVERVIEW} />
                    <Tab label="Trades" value={TAB_TRADES} />
                </Tabs>

                {tab === TAB_OVERVIEW ? (
                    <Stack spacing={2.5}>
                        <PnlCalendarHeatMap
                            from={from}
                            to={to}
                            days={days}
                            totals={dailyTotals}
                            loading={dailyLoading}
                            error={dailyError}
                            onClickDay={handleDayClick}
                            selectedDay={selectedDay}
                        />

                        {selectedDay ? (
                            <>
                                <Divider />
                                <Stack spacing={0.5}>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        {formatSelectedDayLabel(selectedDay)}
                                    </Typography>
                                    {selectedDaySummary ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Day net {formatCurrency(selectedDaySummary.netPnl)}
                                            {' · '}
                                            {selectedDaySummary.tradeCount} round trip
                                            {selectedDaySummary.tradeCount === 1 ? '' : 's'}
                                            {selectedDaySummary.grossPnl != null
                                                ? ` · Gross ${formatCurrency(selectedDaySummary.grossPnl)}`
                                                : ''}
                                            {selectedDaySummary.tcaCost != null
                                                ? ` · TCA ${formatCurrency(selectedDaySummary.tcaCost)}`
                                                : ''}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No summary for this day in the current range.
                                        </Typography>
                                    )}
                                </Stack>
                                <HistoricalTradesTable
                                    strategyId={strategyId}
                                    from={selectedDay}
                                    to={selectedDay}
                                    trades={dayTrades}
                                    totals={dayTradeTotals}
                                    loading={dayTradesLoading}
                                    error={dayTradesError}
                                />
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Select a highlighted day in the calendar to browse that session&apos;s trades here.
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <HistoricalTradesTable
                        strategyId={strategyId}
                        from={from}
                        to={to}
                        trades={rangeTrades}
                        totals={rangeTradeTotals}
                        loading={rangeTradesLoading}
                        error={rangeTradesError}
                    />
                )}
            </Stack>
        </SectionFrame>
    );
};

export default TradeArchivePanel;
